import { GetItemsProps } from 'medere-ui-framework'
import imageCompression from 'browser-image-compression'
import { axiosInstance } from '../../App'
import { ComboDataDTO } from '../../dto/ComboDataDTO'
import {
  defaultUsersFormDTO,
  defaultUsersLocationContextDTO,
  UsersFormDTO,
  UsersConversionCandidateDTO,
  UsersHealthEntityOptionDTO,
  UsersListItemDTO,
  UsersListQueryParams,
  UsersLocationContextDTO,
  UsersProfileOptionDTO,
  UsersSaveDTO,
} from '../../dto/users/UsersDTO'

type UnknownRecord = Record<string, unknown>

interface ComboLike {
  id?: unknown
  value?: unknown
  key?: unknown
  comboId?: unknown
  identifier?: unknown
  low?: unknown
  intValue?: unknown
  intCompact?: unknown
  _value?: unknown
  name?: unknown
  description?: unknown
  label?: unknown
  profile?: unknown
  language?: unknown
  shortName?: unknown
  documentType?: unknown
}

const imageCompressionOptions = {
  maxSizeMB: 1,
  maxWidthOrHeight: 1920,
  useWebWorker: true,
}

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null
}

function toRecord(value: unknown): UnknownRecord | null {
  return isRecord(value) ? value : null
}

function toStringSafe(value: unknown): string {
  return typeof value === 'string' ? value.trim() : String(value ?? '').trim()
}

function parseNumber(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null
  }
  if (typeof value === 'string') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

function parsePositiveNumber(value: unknown): number | null {
  const parsed = parseNumber(value)
  return parsed != null && parsed > 0 ? parsed : null
}

function parseBoolean(value: unknown, fallback = false): boolean {
  if (typeof value === 'boolean') return value
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    if (normalized === 'true') return true
    if (normalized === 'false') return false
  }
  if (typeof value === 'number') return value === 1
  return fallback
}

function extractArray(rawData: unknown): unknown[] {
  if (Array.isArray(rawData)) return rawData
  const rawObj = toRecord(rawData)
  if (!rawObj) return []
  if (Array.isArray(rawObj.data)) return rawObj.data
  if (Array.isArray(rawObj.items)) return rawObj.items
  if (Array.isArray(rawObj.content)) return rawObj.content
  return []
}

function parseComboId(rawId: unknown): number {
  if (rawId == null) return -1
  if (typeof rawId === 'number') return Number.isFinite(rawId) ? rawId : -1
  if (typeof rawId === 'string') {
    const parsed = Number(rawId)
    return Number.isFinite(parsed) ? parsed : -1
  }
  const rawObj = toRecord(rawId)
  if (!rawObj) return -1
  const candidates = [rawObj.id, rawObj.value, rawObj.low, rawObj.intValue, rawObj.intCompact, rawObj._value]
  for (const candidate of candidates) {
    const parsed = parseComboId(candidate)
    if (parsed > 0) return parsed
  }
  return -1
}

function toComboDTO(item: unknown): ComboDataDTO | null {
  const rawObj = toRecord(item) as ComboLike | null
  if (!rawObj) return null
  const id = parseComboId(rawObj.id ?? rawObj.value ?? rawObj.key ?? rawObj.comboId ?? rawObj.identifier ?? rawObj.documentType)
  const name = toStringSafe(rawObj.name ?? rawObj.description ?? rawObj.label ?? rawObj.profile ?? rawObj.language ?? rawObj.shortName)
  if (id <= 0 || name === '') return null
  return { id, name }
}

function parseDateValue(value: unknown): Date | null {
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = new Date(value)
    return Number.isNaN(parsed.getTime()) ? null : parsed
  }
  return null
}

function formatLocalDate(value: Date | null): string | null {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return null
  return value.toISOString().slice(0, 10)
}

function extractTagCodes(item: unknown): string[] {
  const rawObj = toRecord(item)
  if (!rawObj) return []

  const rawTags = rawObj.tagCodes ?? rawObj.tags ?? rawObj.profileTags ?? rawObj.selectedProfileTags
  if (!Array.isArray(rawTags)) return []

  return rawTags
    .map((tag) => toStringSafe(tag).toUpperCase())
    .filter((tag) => tag !== '')
}

function toProfileOptionDTO(item: unknown): UsersProfileOptionDTO | null {
  const combo = toComboDTO(item)
  if (!combo) return null
  return {
    ...combo,
    tagCodes: extractTagCodes(item),
  }
}

function normalizeComboItems(rawData: unknown): ComboDataDTO[] {
  return extractArray(rawData)
    .map((item) => toComboDTO(item))
    .filter((item): item is ComboDataDTO => item !== null)
}

function normalizeProfileItems(rawData: unknown): UsersProfileOptionDTO[] {
  return extractArray(rawData)
    .map((item) => toProfileOptionDTO(item))
    .filter((item): item is UsersProfileOptionDTO => item !== null)
}

function normalizeHealthEntities(rawData: unknown): UsersHealthEntityOptionDTO[] {
  return extractArray(rawData)
    .map((item) => toComboDTO(item))
    .filter((item): item is UsersHealthEntityOptionDTO => item !== null)
}

function normalizeConversionCandidates(rawData: unknown): UsersConversionCandidateDTO[] {
  return extractArray(rawData)
    .map((item) => toRecord(item))
    .filter((item): item is UnknownRecord => item !== null)
    .map((item) => ({
      medereEntityId: parsePositiveNumber(item.medereEntityId ?? item.medereentityid ?? item.medereentity) ?? -1,
      userId: parsePositiveNumber(item.userId ?? item.userid ?? item.user ?? item.medereentityuserid),
      hasUser: parseBoolean(item.hasUser ?? item.hasuser, false) || parsePositiveNumber(item.userId ?? item.userid ?? item.user ?? item.medereentityuserid) != null,
      recordType: toStringSafe(item.recordType ?? item.recordtype ?? item.typedesc),
      onlyPatient: parseBoolean(item.onlyPatient ?? item.onlypatient ?? item.isOnlyPatient ?? item.isonlypatient, true),
      username: toStringSafe(item.username),
      firstname: toStringSafe(item.firstname),
      lastname: toStringSafe(item.lastname),
      document: toStringSafe(item.document),
      documentType: toStringSafe(item.documentType ?? item.documenttype),
      email: toStringSafe(item.email),
      phoneNumber: toStringSafe(item.phoneNumber ?? item.phonenumber),
      mobilePhone: toStringSafe(item.mobilePhone ?? item.mobilephone),
      gender: toStringSafe(item.gender),
      birthDate: parseDateValue(item.birthDate ?? item.birthdate),
      address: toStringSafe(item.address),
      country: toComboDTO(item.country) ?? null,
      province: toComboDTO(item.province) ?? null,
      city: toComboDTO(item.city) ?? null,
      active: parseBoolean(item.active, true),
    }))
    .filter((item) => item.medereEntityId > 0)
}

function normalizeListItem(rawItem: unknown): UsersListItemDTO | null {
  const item = toRecord(rawItem)
  if (!item) return null
  const userId = parsePositiveNumber(item.userId ?? item.userid ?? item.user ?? item._user)
  if (userId == null) return null

  return {
    userId,
    medereEntityId: parsePositiveNumber(item.medereEntityId ?? item.medereentityid ?? item.medereentity),
    username: toStringSafe(item.username),
    firstname: toStringSafe(item.firstname),
    lastname: toStringSafe(item.lastname),
    document: toStringSafe(item.document),
    email: toStringSafe(item.email),
    profileId: parsePositiveNumber(item.profileId ?? item.profileid),
    profile: toStringSafe(item.profile),
    blocked: parseBoolean(item.blocked),
    externalId: toStringSafe(item.externalId ?? item.externalid),
    pictureRoute: toStringSafe(item.pictureRoute ?? item.pictureroute),
    languageId: parsePositiveNumber(item.languageId ?? item.languageid),
    languageName: toStringSafe(item.languageName ?? item.languagename ?? item.language),
  }
}

function normalizeLocationContext(rawData: unknown): UsersLocationContextDTO {
  const item = toRecord(rawData)
  if (!item) return defaultUsersLocationContextDTO

  return {
    country: toComboDTO(item.country) ?? defaultUsersLocationContextDTO.country,
    province: toComboDTO(item.province) ?? defaultUsersLocationContextDTO.province,
    city: toComboDTO(item.city) ?? defaultUsersLocationContextDTO.city,
  }
}

function normalizeFormDTO(rawData: unknown): UsersFormDTO {
  const item = toRecord(rawData)
  if (!item) return defaultUsersFormDTO

  const profile = toComboDTO(item.profile) ?? toComboDTO({
    id: item.profileId ?? item.profileid,
    name: item.profileName ?? item.profilename ?? item.profile,
  })

  const language = toComboDTO(item.language) ?? toComboDTO({
    id: item.languageId ?? item.languageid,
    name: item.languageName ?? item.languagename ?? item.language,
  })

  const locationContext = normalizeLocationContext(item.locationContext ?? item)

  return {
    userId: parsePositiveNumber(item.userId ?? item.userid ?? item.user ?? item._user) ?? -1,
    medereEntityId: parsePositiveNumber(item.medereEntityId ?? item.medereentityid ?? item.medereentity),
    username: toStringSafe(item.username),
    firstname: toStringSafe(item.firstname),
    lastname: toStringSafe(item.lastname),
    email: toStringSafe(item.email),
    profile,
    language,
    blocked: parseBoolean(item.blocked),
    externalId: toStringSafe(item.externalId ?? item.externalid),
    documentType: toComboDTO(item.documentType ?? item.documenttype) ?? defaultUsersFormDTO.documentType,
    document: toStringSafe(item.document),
    gender: (() => {
      const gender = toStringSafe(item.gender).toUpperCase()
      if (gender === 'F') return 'F'
      if (gender === 'M') return 'M'
      return ''
    })(),
    birthDate: parseDateValue(item.birthDate ?? item.birthdate),
    consentSigningDate: parseDateValue(item.consentSigningDate ?? item.consent_signing_date ?? item.consentsigningdate),
    country: locationContext.country,
    province: locationContext.province,
    city: toComboDTO(item.city) ?? locationContext.city,
    address: toStringSafe(item.address),
    mobilePhone: toStringSafe(item.mobilePhone ?? item.mobilephone),
    healthEntities: normalizeHealthEntities(item.healthEntities ?? item.entities ?? item.coverages),
  }
}

function parseSavedUserId(rawData: unknown): number {
  const direct = parsePositiveNumber(rawData)
  if (direct != null) return direct

  const rawObj = toRecord(rawData)
  if (rawObj) {
    const candidates = [rawObj.userId, rawObj.userid, rawObj.user, rawObj.id, rawObj.value, rawObj.data]
    for (const candidate of candidates) {
      const parsed = parsePositiveNumber(candidate)
      if (parsed != null) return parsed
    }
  }

  throw new Error('No se pudo obtener el id del usuario guardado')
}

function extractEffectiveFilters(filterDTO: unknown, cardFilterDTO: unknown): unknown {
  const pick = (value: unknown): unknown | null => {
    const record = toRecord(value)
    if (!record) return null
    const filters = Array.isArray(record.filters) ? record.filters : []
    const filterLists = Array.isArray(record.filterLists) ? record.filterLists : []
    if (filters.length === 0 && filterLists.length === 0) return null
    return value
  }

  return pick(filterDTO) ?? pick(cardFilterDTO) ?? null
}

export function getUsersList(
  offSet: number,
  pageSize: number,
  params: UsersListQueryParams = {},
): Promise<UsersListItemDTO[]> {
  const filters = extractEffectiveFilters(params.filterDTO, params.cardFilterDTO)

  return axiosInstance.post('/api/secured/users-entities/list', {
    scope: params.scope ?? 'admin',
    loadConfigDTO: {
      offSet,
      pageSize,
      sortingFieldInfoList: params.multiSortMeta,
      filters,
    },
    profileFilter: params.profileFilter ?? [],
    blockedFilter: params.blockedFilter ?? [],
  }).then((response) => extractArray(response.data)
    .map((item) => normalizeListItem(item))
    .filter((item): item is UsersListItemDTO => item !== null))
}

export function getUserById(userId: number): Promise<UsersFormDTO> {
  return axiosInstance.get(`/api/secured/users-entities/${userId}`)
    .then((response) => normalizeFormDTO(response.data))
}

export function saveUser(payload: UsersSaveDTO): Promise<number> {
  return axiosInstance.post('/api/secured/users-entities/save', payload)
    .then((response) => parseSavedUserId(response.data))
}

export function deleteUser(userId: number): Promise<void> {
  return axiosInstance.delete(`/api/secured/users-entities/${userId}`)
    .then(() => undefined)
}

export function markUserInactive(userId: number): Promise<void> {
  return axiosInstance.put('/api/secured/inactiveusers/updateInactiveUsers', {
    inactiveUserDTOList: [
      {
        _user: userId,
      },
    ],
  }).then(() => undefined)
}

export function getUserProfiles({ filterValue, offSet, pageSize }: GetItemsProps): Promise<UsersProfileOptionDTO[]> {
  return axiosInstance.get('/api/secured/users-entities/profiles', {
    params: {
      inputText: filterValue ?? '',
      offSet,
      pageSize,
    },
  }).then((response) => normalizeProfileItems(response.data))
}

export function getUserHealthEntities({ filterValue, offSet, pageSize }: GetItemsProps): Promise<UsersHealthEntityOptionDTO[]> {
  return axiosInstance.get('/api/secured/users-entities/health-entities', {
    params: {
      inputText: filterValue ?? '',
      offSet,
      pageSize,
    },
    }).then((response) => normalizeHealthEntities(response.data))
}

export function findUsersConversionCandidates(documentTypeId: number, document: string): Promise<UsersConversionCandidateDTO[]> {
  return axiosInstance.get('/api/secured/users-entities/conversion/candidates', {
    params: {
      documentTypeId,
      document,
      offSet: 0,
      pageSize: 20,
    },
  }).then((response) => normalizeConversionCandidates(response.data))
}

export function getUserDocumentTypes({ filterValue, offSet, pageSize }: GetItemsProps): Promise<ComboDataDTO[]> {
  return axiosInstance.get('/api/secured/users-entities/document-types', {
    params: {
      inputText: filterValue ?? '',
      offSet,
      pageSize,
    },
  }).then((response) => normalizeComboItems(response.data))
}

export function getUserCountries({ filterValue, offSet, pageSize }: GetItemsProps): Promise<ComboDataDTO[]> {
  return axiosInstance.get('/api/secured/users-entities/countries', {
    params: {
      inputText: filterValue ?? '',
      offSet,
      pageSize,
    },
  }).then((response) => normalizeComboItems(response.data))
}

export function getUserProvinces({
  filterValue,
  offSet,
  pageSize,
  params,
}: GetItemsProps<{ countryId?: number }>): Promise<ComboDataDTO[]> {
  if (!params?.countryId || params.countryId <= 0) {
    return Promise.resolve([])
  }

  return axiosInstance.get('/api/secured/users-entities/provinces', {
    params: {
      inputText: filterValue ?? '',
      offSet,
      pageSize,
      countryId: params.countryId,
    },
  }).then((response) => normalizeComboItems(response.data))
}

export function getUserCitiesByProvince({
  filterValue,
  offSet,
  pageSize,
  params,
}: GetItemsProps<{ provinceId?: number }>): Promise<ComboDataDTO[]> {
  if (!params?.provinceId || params.provinceId <= 0) {
    return Promise.resolve([])
  }

  return axiosInstance.get('/api/secured/users-entities/cities', {
    params: {
      inputText: filterValue ?? '',
      offSet,
      pageSize,
      provinceId: params.provinceId,
    },
  }).then((response) => normalizeComboItems(response.data))
}

export function getUserCityContext(cityId: number): Promise<UsersLocationContextDTO> {
  return axiosInstance.get('/api/secured/users-entities/city-context', {
    params: { cityId },
  }).then((response) => normalizeLocationContext(response.data))
}

export function getUserDefaultLocationContext(): Promise<UsersLocationContextDTO> {
  return axiosInstance.get('/api/secured/users-entities/default-location-context')
    .then((response) => normalizeLocationContext(response.data))
}

export function loadUserSignature(userId: number): Promise<string> {
  return axiosInstance.get(`/api/secured/users-entities/${userId}/signature`, { responseType: 'blob' })
    .then((response) => URL.createObjectURL(response.data))
}

export function getUserSignatureBlob(userId: number): Promise<Blob> {
  return axiosInstance.get(`/api/secured/users-entities/${userId}/signature`, { responseType: 'blob' })
    .then((response) => response.data as Blob)
}

export function uploadUserSignature(userId: number, file: File): Promise<void> {
  const formData = new FormData()
  const fileName = typeof file.name === 'string' && file.name.trim() !== '' ? file.name : 'signature.png'
  formData.append('signature', file, fileName)

  return axiosInstance.post(`/api/secured/users-entities/${userId}/uploadSignature`, formData)
    .then(() => undefined)
}

export function deleteUserSignature(userId: number): Promise<void> {
  return axiosInstance.delete(`/api/secured/users-entities/${userId}/signature`)
    .then(() => undefined)
}

export function buildUsersSavePayload(form: UsersFormDTO, password: string, confirmPassword: string): UsersSaveDTO {
  return {
    userId: form.userId,
    medereEntityId: form.medereEntityId,
    username: form.username.trim(),
    firstname: form.firstname.trim(),
    lastname: form.lastname.trim(),
    email: form.email.trim(),
    profileId: form.profile?.id != null && form.profile.id > 0 ? form.profile.id : null,
    languageId: null,
    blocked: form.blocked,
    externalId: form.externalId.trim(),
    documentTypeId: form.documentType?.id != null && form.documentType.id > 0 ? form.documentType.id : null,
    document: form.document.trim(),
    gender: form.gender,
    birthDate: formatLocalDate(form.birthDate),
    cityId: form.city?.id != null && form.city.id > 0 ? form.city.id : null,
    address: form.address.trim(),
    mobilePhone: form.mobilePhone.trim(),
    password,
    confirmPassword,
    healthEntityIds: form.healthEntities
      .map((item) => item.id)
      .filter((id): id is number => id != null && id > 0),
  }
}

export function useUsersService() {
  return {
    getUsersList,
    getUserById,
    saveUser,
    deleteUser,
    markUserInactive,
    getUserProfiles,
    getUserHealthEntities,
    findUsersConversionCandidates,
    getUserDocumentTypes,
    getUserCountries,
    getUserProvinces,
    getUserCitiesByProvince,
    getUserCityContext,
    getUserDefaultLocationContext,
    loadUserSignature,
    getUserSignatureBlob,
    uploadUserSignature,
    deleteUserSignature,
    buildUsersSavePayload,
  }
}
